import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { settingsApi, type NavItemBody } from './api';
import { keys } from '@/lib/query/keys';
import { useApiError } from '@/lib/query/useApiError';
import { DataView } from '@/lib/ui/states';
import { Card } from '@/lib/ui/Card';
import { Button } from '@/lib/ui/Button';
import { Dialog } from '@/lib/ui/Dialog';
import { Select } from '@/lib/ui/Select';
import { TextInput } from '@/lib/ui/Field';
import { Switch } from '@/lib/ui/Switch';
import { MonoValue } from '@/lib/ui/Typography';
import { Tag } from '@/lib/ui/Tag';
import { I18nField } from '@/lib/forms/widgets/I18nField';
import { useToast } from '@/lib/ui';
import type { I18n } from '@/lib/api/types';

interface Draft {
  id: string | null;
  parent_id: string | null;
  label: I18n;
  link_type: 'internal' | 'external';
  target_slug: string;
  url: string;
  open_in_new_tab: boolean;
  is_visible: boolean;
  sort_order: number;
}

/** Navigation tab — manage a menu's items, ordering, and visibility (spec §15.2). */
export function NavigationTab() {
  const { t } = useTranslation();
  const [menuKey, setMenuKey] = useState('header');
  const qc = useQueryClient();
  const toast = useToast();
  const onError = useApiError();

  const MENU_OPTIONS = [
    { value: 'header', label: t('settings.navigation.menuHeader') },
    { value: 'footer', label: t('settings.navigation.menuFooter') },
    { value: 'mobile', label: t('settings.navigation.menuMobile') },
    { value: 'legal', label: t('settings.navigation.menuLegal') },
  ];

  const LINK_TYPE_OPTIONS = [
    { value: 'internal', label: t('settings.navigation.linkInternal') },
    { value: 'external', label: t('settings.navigation.linkExternal') },
  ];

  const query = useQuery({
    queryKey: keys.settings.navigation(menuKey),
    queryFn: () => settingsApi.getNavigation(menuKey),
  });

  const [draft, setDraft] = useState<Draft | null>(null);
  const [errors, setErrors] = useState<Record<string, string>>({});

  const invalidate = () =>
    qc.invalidateQueries({ queryKey: keys.settings.navigation(menuKey) });

  const buildBody = (d: Draft): NavItemBody => ({
    parent_id: d.parent_id,
    label: d.label,
    link_type: d.link_type,
    target_slug: d.link_type === 'internal' ? d.target_slug.trim() : '',
    url: d.link_type === 'external' ? d.url.trim() : '',
    open_in_new_tab: d.open_in_new_tab,
    sort_order: d.sort_order,
    is_visible: d.is_visible,
  });

  const save = useMutation({
    mutationFn: (d: Draft) => settingsApi.updateNavItem(d.id!, buildBody(d)),
    onSuccess: () => {
      void invalidate();
      toast.success(t('settings.navigation.savedTitle'), t('settings.navigation.savedDetail'));
      setDraft(null);
    },
    onError: (e) => onError(e, { onValidation: setErrors }),
  });

  const reorder = useMutation({
    mutationFn: (ids: string[]) => settingsApi.reorderNav(menuKey, ids),
    onSuccess: () => void invalidate(),
    onError: (e) => onError(e),
  });

  const ordered = useMemo(
    () => (query.data ? [...query.data].sort((a, b) => a.sort_order - b.sort_order) : []),
    [query.data],
  );

  const move = (index: number, dir: -1 | 1) => {
    const next = [...ordered];
    const target = index + dir;
    if (target < 0 || target >= next.length) return;
    [next[index], next[target]] = [next[target], next[index]];
    reorder.mutate(next.map((i) => i.id));
  };

  const submit = () => {
    if (!draft) return;
    const next: Record<string, string> = {};
    if (!draft.label.en.trim()) next['label.en'] = t('settings.navigation.errLabelRequired');
    if (draft.link_type === 'internal' && !draft.target_slug.trim())
      next.target_slug = t('settings.navigation.errSlugRequired');
    if (draft.link_type === 'external' && !draft.url.trim())
      next.url = t('settings.navigation.errUrlRequired');
    setErrors(next);
    if (Object.keys(next).length) return;
    save.mutate(draft);
  };

  return (
    <div>
      <div className="mb-md flex flex-wrap items-end gap-md">
        <div className="w-[200px]">
          <Select
            label={t('settings.navigation.menuLabel')}
            value={menuKey}
            onValueChange={setMenuKey}
            options={MENU_OPTIONS}
          />
        </div>
      </div>

      <DataView query={query} isEmpty={(d) => d.length === 0}>
        {() => (
          <div className="flex flex-col gap-xs">
            {ordered.map((item, i) => (
              <Card key={item.id} className="flex items-center justify-between gap-md py-sm">
                <div className="flex items-center gap-sm">
                  <div className="flex flex-col">
                    <Button
                      variant="ghost"
                      size="sm"
                      aria-label={t('settings.navigation.moveUp')}
                      disabled={i === 0 || reorder.isPending}
                      onClick={() => move(i, -1)}
                    >
                      ↑
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      aria-label={t('settings.navigation.moveDown')}
                      disabled={i === ordered.length - 1 || reorder.isPending}
                      onClick={() => move(i, 1)}
                    >
                      ↓
                    </Button>
                  </div>
                  <div className="flex flex-col gap-2xs">
                    <span className="font-medium text-ink-deep">
                      {item.label.en || t('settings.navigation.untitled')}
                    </span>
                    <span className="flex items-center gap-xs text-small text-ink-mute">
                      <Tag variant="neutral">{item.link_type}</Tag>
                      <MonoValue>
                        {item.link_type === 'internal' ? item.target_slug : item.url}
                      </MonoValue>
                      {item.parent_id && <Tag variant="neutral">{t('settings.navigation.childTag')}</Tag>}
                      {!item.is_visible && <Tag variant="amber">{t('settings.navigation.hiddenTag')}</Tag>}
                    </span>
                  </div>
                </div>
                <div className="flex items-center gap-xs">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      setErrors({});
                      setDraft({
                        id: item.id,
                        parent_id: item.parent_id,
                        label: item.label,
                        link_type: item.link_type,
                        target_slug: item.target_slug,
                        url: item.url,
                        open_in_new_tab: item.open_in_new_tab,
                        is_visible: item.is_visible,
                        sort_order: item.sort_order,
                      });
                    }}
                  >
                    {t('common.edit')}
                  </Button>
                </div>
              </Card>
            ))}
          </div>
        )}
      </DataView>

      <Dialog
        open={!!draft}
        onOpenChange={(o) => !o && setDraft(null)}
        title={t('settings.navigation.editItem')}
        footer={
          <>
            <Button variant="ghost" onClick={() => setDraft(null)}>
              {t('common.cancel')}
            </Button>
            <Button loading={save.isPending} onClick={submit}>
              {t('common.save')}
            </Button>
          </>
        }
      >
        {draft && (
          <div className="flex flex-col gap-md">
            <I18nField
              label={t('settings.navigation.fieldLabel')}
              value={draft.label}
              onChange={(label) => setDraft({ ...draft, label })}
              required
              error={errors['label.en']}
            />
            <Select
              label={t('settings.navigation.linkType')}
              value={draft.link_type}
              onValueChange={(v) =>
                setDraft({ ...draft, link_type: v as 'internal' | 'external' })
              }
              options={LINK_TYPE_OPTIONS}
            />
            {draft.link_type === 'internal' ? (
              <TextInput
                label={t('settings.navigation.targetSlug')}
                required
                value={draft.target_slug}
                onChange={(e) => setDraft({ ...draft, target_slug: e.target.value })}
                error={errors.target_slug}
                hint={t('settings.navigation.targetSlugHint')}
              />
            ) : (
              <TextInput
                label={t('settings.navigation.url')}
                required
                value={draft.url}
                onChange={(e) => setDraft({ ...draft, url: e.target.value })}
                error={errors.url}
                hint={t('settings.navigation.urlHint')}
              />
            )}
            <Switch
              label={t('settings.navigation.openInNewTab')}
              checked={draft.open_in_new_tab}
              onCheckedChange={(v) => setDraft({ ...draft, open_in_new_tab: v })}
            />
            <Switch
              label={t('settings.navigation.visible')}
              checked={draft.is_visible}
              onCheckedChange={(v) => setDraft({ ...draft, is_visible: v })}
            />
          </div>
        )}
      </Dialog>
    </div>
  );
}
